<template>
    <gd-modal :name="name" width="1040px" max-width="100%" bg-type="regular" hide-header>
        <div class="afm">
            <header class="afm__head">
                <div class="afm__head-text">
                    <h2 class="afm__title">{{ title }}</h2>
                    <p class="afm__subtitle">
                        <span v-if="activeCount === 0">{{ subtitleEmpty }}</span>
                        <span v-else class="afm__count">{{ activeCount }} {{ activeCount === 1 ? 'filter' : 'filters' }} active</span>
                    </p>
                </div>
                <button type="button" class="afm__close" aria-label="Close" @click="close">
                    <cross-icon class="afm__close-icon" />
                </button>
            </header>

            <div class="afm__body">
                <slot />
            </div>

            <footer class="afm__foot">
                <button type="button" class="afm__btn afm__btn--ghost" :disabled="activeCount === 0" @click="$emit('reset')">
                    <reset-icon class="afm__btn-icon" />Reset to defaults
                </button>
                <div class="afm__foot-spacer"></div>
                <button type="button" class="afm__btn afm__btn--ghost" @click="close">Cancel</button>
                <button type="button" class="afm__btn afm__btn--primary" :disabled="!canApply" @click="$emit('apply')">
                    {{ applyLabel }}
                </button>
            </footer>
        </div>
    </gd-modal>
</template>
<script>
import CrossIcon from '@/assets/images/icons/CrossIcon.svg';
import ResetIcon from '@/assets/images/icons/ResetIcon.svg';

// Shared chrome for the transaction filter modals: header with a live
// active-filter count, scrolling body, sticky footer with reset / apply.
export default {
    name: 'FilterModalShell',
    components: { CrossIcon, ResetIcon },
    props: {
        name: { type: String, required: true },
        title: { type: String, required: true },
        subtitleEmpty: { type: String, default: 'Narrow the list by funnel, affiliate and transaction type.' },
        activeCount: { type: Number, default: 0 },
        canApply: { type: Boolean, default: false },
        applyLabel: { type: String, default: 'Apply filters' },
    },
    methods: {
        close() {
            this.$root.$emit('modal-close', this.name);
        },
    },
};
</script>
<style lang="scss">
.gd-modal.advanced-filters-modal > .gd-modal__content > div,
.gd-modal.advanced-failed-filters-modal > .gd-modal__content > div {
    border-radius: 16px;
    box-shadow: 0 24px 64px -12px rgba(24, 34, 48, 0.28), 0 0 0 1px rgba(24, 34, 48, 0.04);
    overflow: hidden;
}

// The kit pads the modal viewport by 2rem on every side; on short windows give that back to the content
@media (max-height: 760px) {
    .gd-modal.advanced-filters-modal > .gd-modal__content,
    .gd-modal.advanced-failed-filters-modal > .gd-modal__content {
        padding-top: 1rem;
        padding-bottom: 1rem;
    }
}

// Design tokens shared by the filter modal and its parts (.afm, .fnp, .afs)
.afm {
    --af-primary: #f22987;
    --af-primary-dark: #e01d7a;
    --af-primary-soft: #fff0f7;
    --af-primary-ring: rgba(242, 41, 135, 0.22);
    --af-fg: #182230;
    --af-muted: #667085;
    --af-faint: #98a2b3;
    --af-border: #e2e8f0;
    --af-border-strong: #cbd5e1;
    --af-surface: #f8fafc;
    --af-surface-2: #f1f5f9;
    --af-radius: 10px;

    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 4rem);
    color: var(--af-fg);
    text-align: left;

    button { font: inherit; color: inherit; }

    &__head {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 22px 28px 18px;
        border-bottom: 1px solid var(--af-border);
        background:
            radial-gradient(120% 160% at 100% 0%, var(--af-primary-soft) 0%, rgba(255, 240, 247, 0) 55%),
            #fff;
    }

    &__head-text { flex: 1 1 auto; min-width: 0; }

    &__title {
        margin: 0;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: -0.01em;
        line-height: 1.2;
    }

    &__subtitle {
        margin: 4px 0 0;
        font-size: 13.5px;
        color: var(--af-muted);
    }

    &__count {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 2px 10px;
        border-radius: 999px;
        background: var(--af-primary-soft);
        color: var(--af-primary);
        font-weight: 700;
        font-size: 12.5px;

        &::before {
            content: '';
            width: 6px;
            height: 6px;
            border-radius: 999px;
            background: currentColor;
        }
    }

    &__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        margin: -4px -6px 0 0;
        border-radius: 999px;
        border: 0;
        background: transparent;
        color: var(--af-muted);
        cursor: pointer;
        transition: background 120ms ease, color 120ms ease;

        &:hover, &:focus-visible { background: rgba(24, 34, 48, 0.06); color: var(--af-fg); outline: none; }
    }

    &__close-icon { width: 18px; height: 18px; fill: currentColor; }

    &__body {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: 24px 28px 28px;
        display: flex;
        flex-direction: column;
        gap: 28px;
        // Scroll shadows: only visible while there is content above/below the fold
        background:
            linear-gradient(#fff 30%, rgba(255, 255, 255, 0)) top / 100% 28px no-repeat,
            linear-gradient(rgba(255, 255, 255, 0), #fff 70%) bottom / 100% 28px no-repeat,
            radial-gradient(farthest-side at 50% 0, rgba(24, 34, 48, 0.14), transparent) top / 100% 14px no-repeat,
            radial-gradient(farthest-side at 50% 100%, rgba(24, 34, 48, 0.14), transparent) bottom / 100% 14px no-repeat,
            #fff;
        background-attachment: local, local, scroll, scroll, scroll;
    }

    // A titled block inside the body
    &__section { display: flex; flex-direction: column; gap: 12px; }

    &__section-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 12px;
    }

    &__section-title {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.005em;
    }

    &__section-hint {
        margin: 0;
        font-size: 13px;
        color: var(--af-muted);
    }

    &__foot {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 14px 28px;
        border-top: 1px solid var(--af-border);
        background: var(--af-surface);
    }

    &__foot-spacer { flex: 1 1 auto; }

    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        border-radius: var(--af-radius);
        border: 1px solid transparent;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease;

        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--af-primary-ring); }
        &:disabled { cursor: default; opacity: 0.45; }

        &--ghost {
            background: transparent;
            color: var(--af-muted);
            &:hover:not(:disabled) { background: rgba(24, 34, 48, 0.06); color: var(--af-fg); }
        }

        &--primary {
            background: var(--af-primary);
            border-color: var(--af-primary);
            color: #fff;
            padding-left: 20px;
            padding-right: 20px;
            box-shadow: 0 8px 20px -6px rgba(242, 41, 135, 0.45);
            &:hover:not(:disabled) { background: var(--af-primary-dark); border-color: var(--af-primary-dark); transform: translateY(-1px); }
            &:disabled { box-shadow: none; }
        }
    }

    &__btn-icon { width: 14px; height: 14px; fill: currentColor; opacity: 0.8; }

    @media (max-width: 760px) {
        &__head { padding: 18px 18px 14px; }
        &__body { padding: 18px; gap: 22px; }
        &__foot { padding: 12px 18px; flex-wrap: wrap; }
        &__foot-spacer { display: none; }
        &__btn--primary { order: -1; flex: 1 1 100%; justify-content: center; }
        &__btn--ghost { flex: 1 1 0; justify-content: center; }
        &__section-head { flex-direction: column; align-items: flex-start; gap: 2px; }
    }

    // Short windows (laptops with the dock/bookmarks bar): tighten vertical rhythm
    @media (max-height: 760px) {
        max-height: calc(100vh - 2rem);
        &__head { padding: 16px 28px 12px; }
        &__title { font-size: 18px; }
        &__body { padding: 16px 28px 20px; gap: 20px; }
        &__foot { padding: 10px 28px; }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after { transition: none !important; }
    }
}
</style>
